import { Injectable } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {first, map} from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { User } from '../models';
import {FuseConfigService} from "../../../@fuse/services/config.service";
import {FuseNavigationService} from "../../../@fuse/components/navigation/navigation.service";
import {PermissionManagerService} from "../security/services/permissionManager.seervice";
import {time} from "html2canvas/dist/types/css/types/time";

@Injectable({ providedIn: 'root' })
export class AccountService {
    private userSubject: BehaviorSubject<User>;
    public user: Observable<User>;
    private loggedIn = new BehaviorSubject<boolean>(false);
    returnUrl: string;

    constructor(
        private router: Router,
        private http: HttpClient,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private permissionManagerService: PermissionManagerService,
        private route: ActivatedRoute,
    ) {
        this.userSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('user')));
        this.user = this.userSubject.asObservable();

        if (this.userSubject.value) {
            this.loggedIn.next(true);
        }
    }

    public get userValue(): User {
        return this.userSubject.value;
    }

    get isLoggedIn() {
        return this.loggedIn.asObservable();
    }

    refreshToken() {
        return this.http.post<any>(`${environment.apiEndpoint}/token/refresh`, {
            "refreshToken": this.userValue.refreshToken
        })
            .pipe(map(response => {
                let refreshToken = response.refreshToken;
                let token = response.token;
                let refreshTokenExpiration = response.refreshTokenExpiration;

                this.userValue.refreshToken = refreshToken;
                this.userValue.refreshTokenExpiration = refreshTokenExpiration;
                this.userValue.token = token;

                localStorage.setItem('user', JSON.stringify(this.userValue));

                this.userSubject.next(this.userValue);

                return this.userValue;
            }));
    }

    login(username, password) {
        return this.http.post<User>(`${environment.apiEndpoint}/auth/login`, { username, password })
            .pipe(map(user => {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(user));

                this.userSubject.next(user);
                this.loggedIn.next(true);

                // this.userService.

                // Configure the layout
                this._fuseConfigService.config = {
                    layout: {
                        navbar   : {
                            hidden: false
                        },
                        toolbar  : {
                            hidden: false
                        },
                        footer   : {
                            hidden: false
                        },
                        sidepanel: {
                            hidden: false
                        }
                    }
                };

                const current = new Date();
                const timestamp = current.getTime()/1000;
                localStorage.setItem('lastActivityTimestamp', timestamp.toString());

                return this.getCurrentAccount().pipe(first())
                    .subscribe((x: User) => {
                        this.permissionManagerService.authAs(x.roles);
                        this.permissionManagerService.updateNavigation();

                        user.username = x.username;
                        user.firstName = x.firstName;
                        user.lastName = x.lastName;
                        user.street = x.street;
                        user.houseNumber = x.houseNumber;
                        user.flatNumber = x.flatNumber;
                        user.city = x.city;
                        user.postCode = x.postCode;
                        user.country = x.country;
                        user.email = x.email;
                        user.phone = x.phone;
                        user.roles = x.roles;
                        localStorage.setItem('user', JSON.stringify(user));
                        this.userSubject.next(user);
                        this.loggedIn.next(true);

                        this.refreshActivityStamp();

                        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/customers';

                        if ((this.returnUrl.length == 0 || this.returnUrl === '/') && !this.hasRole('ROLE_TRAINER')) {
                            this.returnUrl = '/customers';
                        } else if (this.hasRole('ROLE_TRAINER')) {
                            this.returnUrl = '/calendar';
                        }

                        this.router.navigate([this.returnUrl]).then(data => {
                            // this.updateNavigationForCurrentUser();
                        });
                    });
            }));
    }

    refreshActivityStamp() {
        const current = new Date();
        const timestamp = current.getTime()/1000;

        if (this.userValue && timestamp - this.getActivityStamp()>50000 && timestamp - this.getActivityStamp()<86400) {
            localStorage.setItem('lastActivityTimestamp', timestamp.toString());
            this.refreshToken().subscribe((res) => {

            });
        }
    }

    getActivityStamp() {
        return parseInt(localStorage.getItem('lastActivityTimestamp'));
    }

    updateNavigation(roles: string[]) {
        this._fuseNavigationService.updateNavigationItem('operators', {
            'hidden': !this.permissionManagerService.isGranted('Operators', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('users', {
            'hidden': !this.permissionManagerService.isGranted('Users', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('reports', {
            'hidden': !this.permissionManagerService.isGranted('Reports', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('services', {
            'hidden': !this.permissionManagerService.isGranted('Services', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('communication', {
            'hidden': !this.permissionManagerService.isGranted('Communication', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('settings', {
            'hidden': !this.permissionManagerService.isGranted('Settings', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('customers', {
            'hidden': !this.permissionManagerService.isGranted('Customers', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('calendar', {
            'hidden': !this.permissionManagerService.isGranted('Calendar', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('warehouse', {
            'hidden': !this.permissionManagerService.isGranted('Products', 'ANY')
        });

        this._fuseNavigationService.updateNavigationItem('notes', {
            'hidden': !this.permissionManagerService.isGranted('Notes', 'ANY')
        });

        if (this.hasOnlyRole('ROLE_TRAINER'))  {
            this._fuseNavigationService.updateNavigationItem('trainer-dashboard', {
                'hidden': false
            });
        }
    }

    hasRole(role: string) {
        if (this.userValue != null) {
            return this.userValue.roles.includes(role) || this.userValue.roles.includes('ROLE_SUPER_ADMIN') || this.userValue.roles.includes('ROLE_ADMIN');
        }

        return false;
    }

    hasOnlyRole(role: string) {
        if (this.userValue != null) {
            return this.userValue.roles.includes(role);
        }

        return false;
    }

    updateNavigationForCurrentUser() {
        if (this.userValue != null) {
            this.updateNavigation(this.userValue.roles);
        }
    }

    getCurrentAccount() {
        return this.http.get<User>(`${environment.apiEndpoint}/v1/user/get-current`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.userValue.token,
            }),
        });
    }

    loginByCard(cardNumber) {
        return this.http.post<User>(`${environment.apiEndpoint}/v1/auth/card-login`, { }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'SECURITY-CARD': cardNumber
            }),
        })
            .pipe(map(user => {
                // store user details and jwt token in local storage to keep user logged in between page refreshes

                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(user));
                this.userSubject.next(user);
                this.loggedIn.next(true);

                // this.userService.

                // Configure the layout
                this._fuseConfigService.config = {
                    layout: {
                        navbar   : {
                            hidden: false
                        },
                        toolbar  : {
                            hidden: false
                        },
                        footer   : {
                            hidden: false
                        },
                        sidepanel: {
                            hidden: false
                        }
                    }
                };

                return this.getCurrentAccount().pipe(first())
                    .subscribe((x: User) => {
                        this.permissionManagerService.authAs(x.roles);
                        this.permissionManagerService.updateNavigation();

                        user.username = x.username;
                        user.firstName = x.firstName;
                        user.lastName = x.lastName;
                        user.street = x.street;
                        user.houseNumber = x.houseNumber;
                        user.flatNumber = x.flatNumber;
                        user.city = x.city;
                        user.postCode = x.postCode;
                        user.country = x.country;
                        user.email = x.email;
                        user.phone = x.phone;
                        user.roles = x.roles;

                        localStorage.setItem('user', JSON.stringify(user));
                        this.userSubject.next(user);
                        this.loggedIn.next(true);

                        if (this.hasRole('ROLE_TRAINER')) {
                            this.returnUrl = '/calendar';
                        }

                        this.router.navigate([this.returnUrl]).then(data => {
                            // this.updateNavigationForCurrentUser();
                        });
                    });
            }));
    }

    logout() {
        // remove user from local storage and set current user to null
        localStorage.removeItem('user');
        localStorage.removeItem('role');
        localStorage.removeItem('permissions');
        localStorage.removeItem('tabs');
        this.userSubject.next(null);
        this.loggedIn.next(false);
        this.router.navigate(['/login']);
    }

    isUserAllowed(neededRole) {
        return neededRole.includes(neededRole);
    }

    register(user: User) {
        return this.http.post(`${environment.apiEndpoint}/users/register`, user);
    }

    getAll() {
        return this.http.get<User[]>(`${environment.apiEndpoint}/users`);
    }

    getById(id: string) {
        return this.http.get<User>(`${environment.apiEndpoint}/users/${id}`);
    }

    update(id, params) {
        return this.http.put(`${environment.apiEndpoint}/users/${id}`, params)
            .pipe(map(x => {
                // update stored user if the logged in user updated their own record
                if (id == this.userValue.id) {
                    // update local storage
                    const user = { ...this.userValue, ...params };
                    localStorage.setItem('user', JSON.stringify(user));

                    // publish updated user to subscribers
                    this.userSubject.next(user);
                }
                return x;
            }));
    }

    delete(id: string) {
        return this.http.delete(`${environment.apiEndpoint}/users/${id}`)
            .pipe(map(x => {
                // auto logout if the logged in user deleted their own record
                if (id == this.userValue.id) {
                    this.logout();
                }
                return x;
            }));
    }
}
